import React, { useState } from 'react';
import { Save, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';
import { Settings, DEFAULT_SETTINGS } from '../../lib/types';
import { OFFICES, STATIC_AGENTS } from '../../lib/config';
import { useSettings } from '../../hooks/useSettings';

/**
 * SettingsPanel — configures all API endpoints stored in localStorage.
 * Organized into sections: HQ, Offices, Agents, Reports.
 * No hardcoded URLs — all are user-provided.
 */
export default function SettingsPanel() {
  const { settings, saveSettings, resetSettings } = useSettings();
  const [draft, setDraft] = useState<Settings>(JSON.parse(JSON.stringify(settings)));
  const [saved, setSaved] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    hq: true, reports: false, offices: false, officeApps: false, agents: false,
  });

  const toggleSection = (s: string) => setExpandedSections((p) => ({ ...p, [s]: !p[s] }));

  const handleSave = () => {
    saveSettings(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setDraft(JSON.parse(JSON.stringify(DEFAULT_SETTINGS)));
    resetSettings();
  };

  const setHQ = (key: keyof Settings['hq'], value: string) => {
    setDraft((prev) => ({ ...prev, hq: { ...prev.hq, [key]: value } }));
  };

  const setOffice = (officeId: string, key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      offices: {
        ...prev.offices,
        [officeId]: { ...(prev.offices?.[officeId] || {}), [key]: value },
      },
    }));
  };

  const setOfficeApp = (officeId: string, key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      officeApps: {
        ...prev.officeApps,
        [officeId]: { ...(prev.officeApps?.[officeId] || {}), [key]: value },
      },
    }));
  };

  const setAgent = (agentName: string, key: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      agents: {
        ...prev.agents,
        [agentName]: { ...(prev.agents?.[agentName] || {}), [key]: value },
      },
    }));
  };

  const setReports = (key: keyof Settings['reports'], value: string) => {
    setDraft((prev) => ({ ...prev, reports: { ...prev.reports, [key]: value } }));
  };

  return (
    <div className="flex flex-col h-full" data-testid="settings-panel">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <p className="text-xs text-gray-500">
          All endpoints stored in localStorage. No hardcoded URLs.
        </p>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            data-testid="settings-reset-button"
          >
            <RotateCcw size={12} />
            Reset
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-gray-700 transition-colors"
            data-testid="settings-save-button"
          >
            <Save size={12} />
            {saved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-4">
        {/* HQ Settings */}
        <SettingsSection
          title="KAELO HQ"
          expanded={expandedSections.hq}
          onToggle={() => toggleSection('hq')}
          testId="settings-section-hq"
        >
          <FieldRow label="Chat Webhook URL" hint="POST endpoint for HQ chat messages">
            <UrlInput
              value={draft.hq?.chatWebhookUrl || ''}
              onChange={(v) => setHQ('chatWebhookUrl', v)}
              testId="hq-chat-webhook-url"
            />
          </FieldRow>
          <FieldRow label="Chat Poll URL" hint="GET endpoint polled every 3s for new messages">
            <UrlInput
              value={draft.hq?.chatPollUrl || ''}
              onChange={(v) => setHQ('chatPollUrl', v)}
              testId="hq-chat-poll-url"
            />
          </FieldRow>
          <FieldRow label="Tasks API URL" hint="GET endpoint for external task data">
            <UrlInput
              value={draft.hq?.tasksApiUrl || ''}
              onChange={(v) => setHQ('tasksApiUrl', v)}
              testId="hq-tasks-api-url"
            />
          </FieldRow>
          <FieldRow label="Notifications API URL" hint="GET endpoint for external notifications">
            <UrlInput
              value={draft.hq?.notificationsApiUrl || ''}
              onChange={(v) => setHQ('notificationsApiUrl', v)}
              testId="hq-notifications-api-url"
            />
          </FieldRow>
        </SettingsSection>

        {/* Reports */}
        <SettingsSection
          title="Reports"
          expanded={expandedSections.reports}
          onToggle={() => toggleSection('reports')}
          testId="settings-section-reports"
        >
          <FieldRow label="Morning Summary URL" hint="URL returning morning summary text/markdown">
            <UrlInput value={draft.reports?.morningSummaryUrl || ''} onChange={(v) => setReports('morningSummaryUrl', v)} testId="reports-morning-url" />
          </FieldRow>
          <FieldRow label="Daily Intelligence URL" hint="URL returning daily intelligence briefing">
            <UrlInput value={draft.reports?.dailyIntelUrl || ''} onChange={(v) => setReports('dailyIntelUrl', v)} testId="reports-intel-url" />
          </FieldRow>
          <FieldRow label="Self-Audit URL" hint="URL returning self-audit report">
            <UrlInput value={draft.reports?.selfAuditUrl || ''} onChange={(v) => setReports('selfAuditUrl', v)} testId="reports-audit-url" />
          </FieldRow>
          <FieldRow label="Agent Performance URL" hint="URL returning agent performance data">
            <UrlInput value={draft.reports?.agentPerformanceUrl || ''} onChange={(v) => setReports('agentPerformanceUrl', v)} testId="reports-performance-url" />
          </FieldRow>
        </SettingsSection>

        {/* Office Settings */}
        <SettingsSection
          title="Office Chat Endpoints"
          expanded={expandedSections.offices}
          onToggle={() => toggleSection('offices')}
          testId="settings-section-offices"
        >
          {OFFICES.map((office) => (
            <div key={office.id} className="mb-4 pb-4 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: office.color }} />
                <span className="text-xs font-bold text-gray-700">{office.name}</span>
              </div>
              <div className="space-y-2 pl-5">
                <FieldRow label="Chat Webhook URL" hint={`POST to send messages to ${office.name} agent`}>
                  <UrlInput
                    value={draft.offices?.[office.id]?.chatWebhookUrl || ''}
                    onChange={(v) => setOffice(office.id, 'chatWebhookUrl', v)}
                    testId={`office-${office.id}-webhook-url`}
                  />
                </FieldRow>
                <FieldRow label="Chat Poll URL" hint="GET endpoint polled every 3s">
                  <UrlInput
                    value={draft.offices?.[office.id]?.chatPollUrl || ''}
                    onChange={(v) => setOffice(office.id, 'chatPollUrl', v)}
                    testId={`office-${office.id}-poll-url`}
                  />
                </FieldRow>
              </div>
            </div>
          ))}
        </SettingsSection>

        {/* Agent Settings */}
        <SettingsSection
          title="Agent Chat Endpoints"
          expanded={expandedSections.agents}
          onToggle={() => toggleSection('agents')}
          testId="settings-section-agents"
        >
          {STATIC_AGENTS.map((agent) => (
            <div key={agent.name} className="mb-4 pb-4 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                  {agent.name.charAt(0)}
                </div>
                <span className="text-xs font-bold text-gray-700">{agent.name}</span>
                <span className="text-[10px] text-gray-400">{agent.model}</span>
              </div>
              <div className="space-y-2 pl-8">
                <FieldRow label="Chat Webhook URL" hint={`POST to ${agent.name} agent`}>
                  <UrlInput
                    value={draft.agents?.[agent.name]?.chatWebhookUrl || ''}
                    onChange={(v) => setAgent(agent.name, 'chatWebhookUrl', v)}
                    testId={`agent-${agent.name}-webhook-url`}
                  />
                </FieldRow>
                <FieldRow label="Chat Poll URL" hint="GET endpoint for replies">
                  <UrlInput
                    value={draft.agents?.[agent.name]?.chatPollUrl || ''}
                    onChange={(v) => setAgent(agent.name, 'chatPollUrl', v)}
                    testId={`agent-${agent.name}-poll-url`}
                  />
                </FieldRow>
              </div>
            </div>
          ))}
        </SettingsSection>

        {/* Office App API Endpoints */}
        <SettingsSection
          title="Office App APIs"
          expanded={expandedSections.officeApps}
          onToggle={() => toggleSection('officeApps')}
          testId="settings-section-officeApps"
        >
          <p className="text-xs text-gray-400 mb-4 leading-relaxed">
            Configure data endpoints for office-specific panels (Budget, Deals, Properties, etc.).
            Each endpoint should return JSON: an array <code className="bg-gray-100 px-1 rounded text-[10px]">[{'{...}'}]</code> or <code className="bg-gray-100 px-1 rounded text-[10px]">{'{ "rows": [...] }'}</code>.
          </p>
          {OFFICES.map((office) => (
            <div key={office.id} className="mb-5 pb-5 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: office.color }} />
                <span className="text-xs font-bold text-gray-700">{office.name}</span>
              </div>
              <div className="space-y-2 pl-5">
                {getOfficeAppFields(office.id).map(({ key, label }) => (
                  <FieldRow key={key} label={label}>
                    <UrlInput
                      value={draft.officeApps?.[office.id]?.[key] || ''}
                      onChange={(v) => setOfficeApp(office.id, key, v)}
                      testId={`officeapp-${office.id}-${key}`}
                    />
                  </FieldRow>
                ))}
              </div>
            </div>
          ))}
        </SettingsSection>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SettingsSection({ title, expanded, onToggle, children, testId }: {
  title: string; expanded: boolean; onToggle: () => void; children: React.ReactNode; testId: string;
}) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={testId}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        data-testid={`${testId}-toggle`}
      >
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        {expanded ? <ChevronDown size={15} className="text-gray-500" /> : <ChevronRight size={15} className="text-gray-500" />}
      </button>
      {expanded && <div className="p-5 space-y-4 bg-white">{children}</div>}
    </div>
  );
}

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {hint && <p className="text-[10px] text-gray-400 mb-1.5">{hint}</p>}
      {children}
    </div>
  );
}

function UrlInput({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId: string }) {
  return (
    <input
      type="url"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="https://..."
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 placeholder:font-sans placeholder:text-gray-400"
      data-testid={testId}
    />
  );
}

// ── Office app field definitions ──────────────────────────────────────────────

const OFFICE_APP_FIELDS: Record<string, Array<{ key: string; label: string }>> = {
  home:         [{ key: 'budgetApiUrl', label: 'Budget API URL' }],
  kaalvoet:     [{ key: 'projectsApiUrl', label: 'Projects API URL' }, { key: 'sacapApiUrl', label: 'SACAP Tracker API URL' }],
  zikhethele:   [{ key: 'dealsApiUrl', label: 'Deal Pipeline API URL' }, { key: 'leadsApiUrl', label: 'Leads API URL' }],
  developments: [{ key: 'hopperApiUrl', label: 'Hopper API URL' }, { key: 'feasibilityApiUrl', label: 'Feasibility API URL' }],
  odimo:        [{ key: 'propertiesApiUrl', label: 'Properties API URL' }, { key: 'tenantsApiUrl', label: 'Tenants API URL' }, { key: 'complianceApiUrl', label: 'Compliance API URL' }],
};

function getOfficeAppFields(officeId: string) {
  return OFFICE_APP_FIELDS[officeId] || [];
}
